import styles from '../less/BaseLayout.module.less'
import {
  Button,
  Card,
  Col,
  ConfigProvider,
  Layout,
  Row,
  Space,
  Steps,
  theme,
  Typography,
} from 'antd'
import { helpCenterLinks } from '../config/helpCenterLinks'
import { readOnboardingSessionToken } from '../lib/onboardingSessionToken'
import { useOnboardingHelpLink } from '../lib/useOnboardingHelpLink'
import { gutter } from '../lib/layout'
import { useLogo } from '../lib/themeController'
import { useFirestore } from 'reactfire'
import { doc, onSnapshot, updateDoc } from 'firebase/firestore'
import { useParams } from 'react-router-dom'
import _times from 'lodash/times'
import * as Sentry from '@sentry/react'
import PageLoading from '../components/PageLoading'
import { StepWelcome } from '../components/Onboarding/StepWelcome'
import { StepCompanyDetailsForm } from '../components/Onboarding/StepCompanyDetailsForm'
import { StepCompanyOperationsForm } from '../components/Onboarding/StepCompanyOperationsForm'
import { StepCompanyAccountingForm } from '../components/Onboarding/StepCompanyAccountingForm'
import { StepCompanyBankingForm } from '../components/Onboarding/StepCompanyBankingForm'
import { StepSoftware } from '../components/Onboarding/StepSoftware'
import { StepUploadCompanyData } from '../components/Onboarding/StepUploadCompanyData'
import { NotFound } from './404'
import { StepDeregistrationReason } from '../components/Onboarding/StepDeregistrationReason'
import { StepUploadPayrollAccounts } from '../components/Onboarding/StepUploadPayrollAccounts'
import { Completed } from '../components/Onboarding/Completed'
import useDocumentTitle from '../lib/useDocumentTitle'
import { Help } from '../components/Help/Help'
import { StepUploadAdditionalDocuments } from '../components/Onboarding/StepUploadAdditionalDocuments'
import { StepUploadDLSDocuments } from '../components/Onboarding/StepUploadDLSDocuments'
import { StepUploadContributionStatements } from '../components/Onboarding/StepUploadContributionStatements'
import { StepUploadHealthInsuranceDocuments } from '../components/Onboarding/StepUploadHealthInsuranceDocuments'
import { StepUploadEmployeeData } from '../components/Onboarding/StepUploadEmployeeData'
import { StepUploadPreviousMonthPayslips } from '../components/Onboarding/StepUploadPreviousMonthPayslips'
import { StepUploadPaymentFile } from '../components/Onboarding/StepUploadPaymentFile'
import { StepUploadAgendaBackup } from '../components/Onboarding/StepUploadAgendaBackup'
import { useSafeIntercom } from '../components/Intercom'
import { StepQuestions } from '../components/Onboarding/StepQuestions'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

const additionalDocumentsU1LevyTypes = ['mixed', 'reduced', 'increased']

export function OnboardingPage() {
  const { companyUid } = useParams()
  const [sessionTokenState, setSessionTokenState] = useState(() => ({
    companyUid,
    token: readOnboardingSessionToken(companyUid),
  }))
  if (sessionTokenState.companyUid !== companyUid) {
    setSessionTokenState({
      companyUid,
      token: readOnboardingSessionToken(companyUid),
    })
  }
  const sessionToken = sessionTokenState.token

  const firestore = useFirestore()
  const { t } = useTranslation()

  // CL-1296: bypass reactfire's useFirestoreDocData for this subscription.
  // The Firestore SDK occasionally throws "Target ID already exists" during
  // listener registration when an authenticated session has stale state.
  // reactfire turns that into a thrown render error which crashes the whole
  // route. Direct onSnapshot delivers the same error to a callback, so we
  // can retry the subscription and render a friendly error instead of the
  // global "Da ist etwas schiefgelaufen" page.
  const [onboardingState, setOnboardingState] = useState({
    status: 'loading',
    data: null,
    error: null,
  })

  useEffect(() => {
    // Reset to loading whenever companyUid changes so the previous tenant's
    // data does not flash under the new URL before the first snapshot
    // arrives.
    setOnboardingState({ status: 'loading', data: null, error: null })

    let cancelled = false
    let unsubscribe = null
    let retryTimer = null
    let retryCount = 0
    const MAX_TARGET_ID_RETRIES = 5

    const safeUnsubscribe = () => {
      if (!unsubscribe) return
      const fn = unsubscribe
      unsubscribe = null
      try {
        fn()
      } catch {
        // best effort
      }
    }

    const handleError = (error) => {
      if (cancelled) return

      const isTargetIdError =
        error?.name === 'FirebaseError' &&
        typeof error?.message === 'string' &&
        error.message.startsWith('Target ID already exists')

      if (isTargetIdError && retryCount < MAX_TARGET_ID_RETRIES) {
        retryCount += 1
        safeUnsubscribe()
        retryTimer = setTimeout(subscribe, 250 * retryCount)
        return
      }

      Sentry.captureException(error, {
        tags: {
          component: 'OnboardingPage',
          subscription: 'onboarding-doc',
          targetIdError: isTargetIdError ? '1' : '0',
        },
      })
      setOnboardingState({ status: 'error', data: null, error })
    }

    const subscribe = () => {
      try {
        unsubscribe = onSnapshot(
          doc(firestore, 'onboarding', companyUid),
          (snap) => {
            if (cancelled) return
            setOnboardingState({
              status: 'success',
              data: snap.exists() ? { ...snap.data(), uid: snap.id } : null,
              error: null,
            })
          },
          handleError
        )
      } catch (error) {
        handleError(error)
      }
    }

    subscribe()

    return () => {
      cancelled = true
      if (retryTimer) clearTimeout(retryTimer)
      safeUnsubscribe()
    }
  }, [firestore, companyUid])

  const onboarding = onboardingState.data

  useEffect(() => {
    if (
      onboarding &&
      onboarding.uid === companyUid &&
      (!onboarding.active || onboarding.completed)
    ) {
      try {
        localStorage.removeItem(`onboarding-token-${companyUid}`)
      } catch {
        // best effort
      }
      setSessionTokenState((prev) =>
        prev.companyUid === companyUid && prev.token === null
          ? prev
          : { companyUid, token: null }
      )
    }
  }, [onboarding, companyUid])

  useDocumentTitle(
    t('frontend:onboarding.pageTitle', {
      name: onboarding?.company?.name ?? '',
    })
  )

  const { boot: bootIntercom, showMessages: showIntercomMessage } =
    useSafeIntercom()
  const { token } = theme.useToken()
  const logo = useLogo('full')
  const onboardingHelpLink = useOnboardingHelpLink(companyUid)

  if (onboardingState.status === 'loading') {
    return <PageLoading />
  }

  if (onboardingState.status === 'error') {
    return (
      <OnboardingLoadError
        error={onboardingState.error}
        onRetry={() => window.location.reload()}
      />
    )
  }

  if (!onboarding) {
    return <NotFound />
  }

  const { currentSection, currentStep } = onboarding

  const newYearStart = onboarding.firstMonth?.endsWith('-01')
  const specialU1Levy =
    additionalDocumentsU1LevyTypes.indexOf(
      onboarding.company?.accounting?.u1Levy
    ) > -1

  // if (newYearStart) {
  // No need for Lohnkonten, DeregistrationReason
  // }
  // if (onboarding.company.accounting.estimator) {
  // Need to upload Beitragsnachweise
  // }
  // TODO

  const finish = async () => {
    await updateDoc(doc(firestore, 'onboarding', companyUid), {
      completed: true,
    })
  }

  const section0 = {
    hideInSteps: true,
    title: '',
    steps: [
      {
        title: '',
        content: (
          <StepWelcome
            key="welcome"
            onboarding={onboarding}
            goToNext={goToNextStep}
          />
        ),
      },
      {
        title: t('frontend:onboarding.stepSoftware'),
        content: (
          <StepSoftware
            key="software"
            onboarding={onboarding}
            goToNext={goToNextStep}
            goToPrevious={goToPreviousStep}
          />
        ),
      },
      {
        title: t('frontend:onboarding.stepDeregistrationReason'),
        content: (
          <StepDeregistrationReason
            key="deregistration-reason"
            onboarding={onboarding}
            goToPrevious={goToPreviousStep}
            goToNext={goToNextStep}
          />
        ),
      },
    ],
  }

  const section1 = {
    title: t('frontend:onboarding.sectionClientMasterData'),
    steps: [
      {
        title: t('frontend:onboarding.stepCompanyDetails'),
        content: (
          <StepCompanyDetailsForm
            key="company-details"
            onboarding={onboarding}
            goToNext={goToNextStep}
            goToPrevious={goToPreviousStep}
          />
        ),
      },
      {
        title: t('frontend:onboarding.stepCompanyOperations'),
        content: (
          <StepCompanyOperationsForm
            key="company-operations"
            onboarding={onboarding}
            goToNext={goToNextStep}
            goToPrevious={goToPreviousStep}
          />
        ),
      },
      {
        title: t('frontend:onboarding.stepCompanyAccounting'),
        content: (
          <StepCompanyAccountingForm
            key="company-accounting"
            onboarding={onboarding}
            goToNext={goToNextStep}
            goToPrevious={goToPreviousStep}
          />
        ),
      },
      {
        title: t('frontend:onboarding.stepCompanyBanking'),
        content: (
          <StepCompanyBankingForm
            key="company-banking"
            onboarding={onboarding}
            goToNext={goToNextStep}
            goToPrevious={goToPreviousStep}
          />
        ),
      },
      {
        title: t('frontend:onboarding.stepFurtherQuestions'),
        content: (
          <StepQuestions
            key="questions"
            onboarding={onboarding}
            goToNext={goToNextStep}
            goToPrevious={goToPreviousStep}
          />
        ),
      },
    ],
  }

  const section2 = {
    title: t('frontend:onboarding.sectionDataUpload'),
    steps: [
      ...(onboarding.software === 'agenda'
        ? [
            {
              title: t('frontend:onboarding.sectionClientMasterData'),
              content: (
                <StepUploadCompanyData
                  key="upload-company-data-pdf"
                  onboarding={onboarding}
                  sessionToken={sessionToken}
                  goToNext={goToNextStep}
                  goToPrevious={goToPreviousStep}
                />
              ),
            },
            {
              title: t('frontend:onboarding.stepEmployeeMasterData'),
              content: (
                <StepUploadEmployeeData
                  key="upload-employee-data"
                  onboarding={onboarding}
                  sessionToken={sessionToken}
                  goToNext={goToNextStep}
                  goToPrevious={goToPreviousStep}
                />
              ),
            },
            {
              title: t('frontend:onboarding.stepPreviousMonthPayslips'),
              content: (
                <StepUploadPreviousMonthPayslips
                  key="upload-previous-month-payslips"
                  onboarding={onboarding}
                  sessionToken={sessionToken}
                  goToNextText={t('frontend:onboarding.next')}
                  goToNext={goToNextStep}
                  goToPrevious={goToPreviousStep}
                />
              ),
            },
            {
              title: t('frontend:onboarding.stepPaymentFile'),
              content: (
                <StepUploadPaymentFile
                  key="upload-payment-file"
                  onboarding={onboarding}
                  sessionToken={sessionToken}
                  goToNextText={t('frontend:onboarding.next')}
                  goToNext={goToNextStep}
                  goToPrevious={goToPreviousStep}
                />
              ),
            },
            {
              title: t('frontend:onboarding.sectionClientMasterData'),
              content: (
                <StepUploadAgendaBackup
                  key="upload-agenda-backup"
                  onboarding={onboarding}
                  sessionToken={sessionToken}
                  goToNextText={t('frontend:onboarding.next')}
                  goToNext={goToNextStep}
                  goToPrevious={goToPreviousStep}
                />
              ),
            },
            ...(onboarding.company?.accounting?.estimator
              ? [
                  {
                    title: t('frontend:onboarding.stepContributionStatements'),
                    content: (
                      <StepUploadContributionStatements
                        key="upload-contribution-statements"
                        onboarding={onboarding}
                        sessionToken={sessionToken}
                        goToNext={goToNextStep}
                        goToPrevious={goToPreviousStep}
                      />
                    ),
                  },
                ]
              : []),
            {
              title: t('frontend:onboarding.stepAdditionalDocuments'),
              content: (
                <StepUploadAdditionalDocuments
                  key="upload-additional-documents"
                  onboarding={onboarding}
                  sessionToken={sessionToken}
                  goToNextText={t('frontend:onboarding.finishHandover')}
                  goToNext={goToNextStep}
                  goToPrevious={goToPreviousStep}
                />
              ),
            },
          ]
        : [
            {
              title: t('frontend:onboarding.sectionClientMasterData'),
              content: (
                <StepUploadCompanyData
                  key="upload-company-data-pdf"
                  onboarding={onboarding}
                  sessionToken={sessionToken}
                  goToNext={goToNextStep}
                  goToPrevious={goToPreviousStep}
                />
              ),
            },
            {
              title: t('frontend:onboarding.stepEmployeeMasterData'),
              content: (
                <StepUploadEmployeeData
                  key="upload-employee-data"
                  onboarding={onboarding}
                  sessionToken={sessionToken}
                  goToNext={goToNextStep}
                  goToPrevious={goToPreviousStep}
                />
              ),
            },
            {
              title: t('frontend:onboarding.stepPreviousMonthPayslips'),
              content: (
                <StepUploadPreviousMonthPayslips
                  key="upload-previous-month-payslips"
                  onboarding={onboarding}
                  sessionToken={sessionToken}
                  goToNextText={t('frontend:onboarding.next')}
                  goToNext={goToNextStep}
                  goToPrevious={goToPreviousStep}
                />
              ),
            },
            {
              title: t('frontend:onboarding.stepPaymentFile'),
              content: (
                <StepUploadPaymentFile
                  key="upload-payment-file"
                  onboarding={onboarding}
                  sessionToken={sessionToken}
                  goToNextText={t('frontend:onboarding.next')}
                  goToNext={goToNextStep}
                  goToPrevious={goToPreviousStep}
                />
              ),
            },
            {
              title: t('frontend:onboarding.stepDlsFiles'),
              content: (
                <StepUploadDLSDocuments
                  key="upload-dls-files"
                  onboarding={onboarding}
                  sessionToken={sessionToken}
                  goToNextText={t('frontend:onboarding.next')}
                  goToNext={goToNextStep}
                  goToPrevious={goToPreviousStep}
                />
              ),
            },
            ...(onboarding.company?.accounting?.estimator
              ? [
                  {
                    title: t('frontend:onboarding.stepContributionStatements'),
                    content: (
                      <StepUploadContributionStatements
                        key="upload-contribution-statements"
                        onboarding={onboarding}
                        sessionToken={sessionToken}
                        goToNext={goToNextStep}
                        goToPrevious={goToPreviousStep}
                      />
                    ),
                  },
                ]
              : []),
            ...(specialU1Levy
              ? [
                  {
                    title: t(
                      'frontend:onboarding.stepHealthInsuranceDocuments'
                    ),
                    content: (
                      <StepUploadHealthInsuranceDocuments
                        key="upload-health-insurance-documents"
                        onboarding={onboarding}
                        sessionToken={sessionToken}
                        goToNext={goToNextStep}
                        goToPrevious={goToPreviousStep}
                      />
                    ),
                  },
                ]
              : []),
            {
              title: t('frontend:onboarding.stepAdditionalDocuments'),
              content: (
                <StepUploadAdditionalDocuments
                  key="upload-additional-documents"
                  onboarding={onboarding}
                  sessionToken={sessionToken}
                  goToNextText={
                    newYearStart
                      ? t('frontend:onboarding.finishHandover')
                      : t('frontend:onboarding.next')
                  }
                  goToNext={goToNextStep}
                  goToPrevious={goToPreviousStep}
                />
              ),
            },
          ]),
    ],
  }

  const section3 = {
    title: t('frontend:onboarding.sectionCarryForwardValues'),
    steps: [
      {
        title: t('frontend:onboarding.stepPayrollAccounts'),
        content: (
          <StepUploadPayrollAccounts
            key="upload-payroll-accounts"
            onboarding={onboarding}
            sessionToken={sessionToken}
            goToNextText={t('frontend:onboarding.finishHandover')}
            goToNext={goToNextStep}
            goToPrevious={goToPreviousStep}
          />
        ),
      },
    ],
  }

  const sections = [
    section0,
    section1,
    section2,
    ...(newYearStart || onboarding.software === 'agenda' ? [] : [section3]),
  ]

  async function setSectionAndStep(obj) {
    await updateDoc(doc(firestore, 'onboarding', companyUid), {
      currentSection: obj.section,
      currentStep: obj.step,
    })
  }

  async function goToNextStep() {
    const activeSection = sections[currentSection]
    if (currentStep < activeSection.steps.length - 1) {
      await setSectionAndStep({
        section: currentSection,
        step: currentStep + 1,
      })
    } else if (currentSection === sections.length - 1) {
      await finish()
    } else {
      await setSectionAndStep({ section: currentSection + 1, step: 0 })
    }
  }

  async function goToPreviousStep() {
    if (currentStep > 0) {
      await setSectionAndStep({
        section: currentSection,
        step: currentStep - 1,
      })
    } else {
      await setSectionAndStep({
        section: currentSection - 1,
        step: sections[currentSection - 1].steps.length - 1,
      })
    }
  }

  const activeSection = sections[currentSection ?? 0] ?? sections[0]

  return (
    <Layout className={styles.baseLayoutOnboarding}>
      <Layout.Header
        className={styles.header}
        style={{
          textAlign: 'center',
          marginTop: token.marginLG,
          marginBottom: token.marginSM,
        }}
      >
        <div className={styles.topMenuOnboarding}>
          <div className={styles.topMenuInner}>
            <img src={logo} alt="cleverlohn" className={styles.logo} />
            <div>
              <Space size="small">
                {t('frontend:onboarding.clientHandover')}{' '}
                <Typography.Text strong>
                  {onboarding.company.name}
                </Typography.Text>
              </Space>
            </div>
            <div style={{ marginLeft: 'auto' }}>
              <Help
                text={t('frontend:onboarding.help')}
                size="large"
                showContactButton
                onContactClicked={() => {
                  bootIntercom()
                  showIntercomMessage()
                }}
                helpLink={onboardingHelpLink(
                  helpCenterLinks.de.onboarding.taxAdvisors
                )}
                inAppHelp={false}
                description={
                  <div>
                    {t('frontend:onboarding.needHelp')}
                    <br />
                    <Typography.Text type="secondary">
                      {t('frontend:onboarding.weAreHappyToHelp')}
                    </Typography.Text>
                  </div>
                }
              />
            </div>
          </div>
        </div>
      </Layout.Header>
      <Layout.Content className={styles.contentOnboarding}>
        <Row gutter={gutter}>
          <Col span={24}>
            {/*<CardHeader*/}
            {/*  style={{*/}
            {/*    textAlign: 'center',*/}
            {/*    marginBottom: token.marginXL,*/}
            {/*  }}*/}
            {/*>*/}
            {/*  <span style={{ fontWeight: 300 }}>Mandantenübernahme</span>{' '}*/}
            {/*  {onboarding.company.name}*/}
            {/*</CardHeader>*/}
            <div
              style={{
                textAlign: 'center',
                marginBottom: token.marginLG,
              }}
            >
              <ConfigProvider
                theme={{
                  token: {
                    // controlItemBgActive: token.colorWhite,
                    // theme-ok: brand lime accent for the onboarding steps
                    colorPrimary: '#aecc43',
                    // colorFillContent: 'blue',
                    // colorSplit: 'magenta',
                    // colorTextLabel: 'orange',
                  },
                }}
              >
                <Steps
                  // size="small"
                  style={{
                    paddingLeft: token.marginSM,
                    paddingRight: token.marginSM,
                  }}
                  responsive={false}
                  // labelPlacement='vertical'
                  current={
                    currentSection === 0
                      ? -1
                      : onboarding.completed
                        ? currentSection
                        : currentSection - 1
                  }
                  items={sections
                    .filter((s) => s != null && !s.hideInSteps)
                    .map((s) => ({ title: s.title }))}
                />
              </ConfigProvider>
              {/*<ProgressDots*/}
              {/*  steps={sections}*/}
              {/*  goToStep={goToStep}*/}
              {/*  wide*/}
              {/*  currentStep={currentSection}*/}
              {/*/>*/}
            </div>
          </Col>
          <Col span={24}>
            <Row gutter={gutter}>
              <ConfigProvider
                theme={{
                  token: {
                    fontSize: 14,
                    paddingLG: token.marginXXL,
                    controlHeight: token.controlHeightLG,
                    borderRadius: token.borderRadiusLG,
                  },
                  components: {
                    // Make all buttons large
                    Button: {
                      fontSize: token.fontSizeLG,
                      paddingContentHorizontal:
                        token.paddingContentHorizontalLG,
                      controlHeight: token.controlHeightLG,
                      borderRadius: token.borderRadiusLG,
                    },
                  },
                }}
              >
                <Col span={24}>
                  <Card bordered={false}>
                    {onboarding.completed ? (
                      <Completed onboarding={onboarding} />
                    ) : (
                      <StepsSection
                        currentSection={currentSection}
                        sections={sections}
                        title={activeSection.title}
                        currentStep={currentStep}
                        steps={activeSection.steps}
                      />
                    )}
                  </Card>
                </Col>
              </ConfigProvider>
              {/*<Col span={8}>*/}
              {/*  <Card bordered={false}>*/}
              {/*    <img*/}
              {/*      style={{*/}
              {/*        position: 'absolute',*/}
              {/*        width: 500,*/}
              {/*        // left: '-70px',*/}
              {/*        top: '-30px',*/}
              {/*      }}*/}
              {/*      src={newEmployeeOnboarding}*/}
              {/*      alt=""*/}
              {/*    />*/}

              {/*    <Space direction="vertical" style={{ marginTop: 320 }}>*/}
              {/*      <Typography.Text>Brauchen Sie Hilfe?</Typography.Text>*/}
              {/*      <Typography.Text type="secondary">*/}
              {/*        Wir unterstützen Sie gerne.*/}
              {/*      </Typography.Text>*/}
              {/*      <Button>Kontakt</Button>*/}
              {/*    </Space>*/}
              {/*  </Card>*/}
              {/*</Col>*/}
            </Row>
          </Col>
        </Row>
      </Layout.Content>
    </Layout>
  )
}

function StepsSection({ title, steps, currentStep }) {
  const step = steps[currentStep ?? 0]
  return (
    <Row gutter={[24, 12]}>
      {title && (
        <Col span={24}>
          <Typography.Title level={4}>
            <Space>
              {title}{' '}
              <Typography.Text type="secondary" style={{ fontWeight: 400 }}>
                {step.title}
              </Typography.Text>
            </Space>
          </Typography.Title>
        </Col>
      )}
      <Col span={24}>{step.content}</Col>
      {steps.length > 1 && (
        <Col span={24} style={{ textAlign: 'center' }}>
          <ProgressDots steps={steps} currentStep={currentStep ?? 0} />
        </Col>
      )}
    </Row>
  )
}

function ProgressDots({ steps, currentStep = 0, wide }) {
  if (steps.length === 1) return null

  const { token } = theme.useToken()
  return (
    <>
      {_times(steps.length, (i) => (
        <div
          key={i}
          // onClick={() => {
          //   if (i >= currentStep) return
          //   goToStep(null, i)
          // }}
          style={{
            cursor: i < currentStep ? 'pointer' : undefined,
            height: `${token.paddingXS}px`,
            width: wide ? `${token.paddingXS * 3}px` : `${token.paddingXS}px`,
            margin: `0 ${token.marginXS}px`,
            backgroundColor:
              currentStep >= i
                ? wide
                  ? token.colorText
                  : token.colorPrimary
                : token.colorFillTertiary,
            borderRadius: `${token.paddingXS}px`,
            display: 'inline-block',
          }}
        />
      ))}
    </>
  )
}

function OnboardingLoadError({ error, onRetry }) {
  const { token } = theme.useToken()
  const logo = useLogo('full')
  const { t } = useTranslation()
  const isTargetIdError =
    error?.name === 'FirebaseError' &&
    typeof error?.message === 'string' &&
    error.message.startsWith('Target ID already exists')

  return (
    <Layout className={styles.baseLayoutOnboarding}>
      <Layout.Header
        className={styles.header}
        style={{
          textAlign: 'center',
          marginTop: token.marginLG,
          marginBottom: token.marginSM,
        }}
      >
        <div className={styles.topMenuOnboarding}>
          <div className={styles.topMenuInner}>
            <img src={logo} alt="cleverlohn" className={styles.logo} />
          </div>
        </div>
      </Layout.Header>
      <Layout.Content className={styles.contentOnboarding}>
        <Row gutter={gutter} justify="center">
          <Col xs={24} md={16} lg={12}>
            <Card bordered={false}>
              <Space direction="vertical" size="large">
                <Typography.Title level={3}>
                  {t('frontend:onboarding.loadErrorTitle')}
                </Typography.Title>
                <Typography.Paragraph>
                  {isTargetIdError
                    ? t('frontend:onboarding.loadErrorTargetId')
                    : t('frontend:onboarding.loadErrorGeneric')}
                </Typography.Paragraph>
                <Button type="primary" size="large" onClick={onRetry}>
                  {t('frontend:onboarding.reloadPage')}
                </Button>
              </Space>
            </Card>
          </Col>
        </Row>
      </Layout.Content>
    </Layout>
  )
}
